/**
 * Log Sanitization Utility
 * 
 * This module provides utilities for sanitizing log messages to prevent
 * sensitive information from being logged.
 */

import validator from 'validator';

/**
 * Types of sensitive data that should be masked in logs
 */
export enum SensitiveDataType {
  USER_ID = 'USER_ID',
  EMAIL = 'EMAIL',
  PASSWORD = 'PASSWORD',
  AUTH_TOKEN = 'AUTH_TOKEN',
  SESSION_ID = 'SESSION_ID',
  PROFILE_DATA = 'PROFILE_DATA',
  RESPONSE_DATA = 'RESPONSE_DATA',
  CREDIT_CARD = 'CREDIT_CARD',
  SSN = 'SSN',
  API_KEY = 'API_KEY',
  DATABASE_CREDENTIALS = 'DATABASE_CREDENTIALS',
  PERSONAL_INFO = 'PERSONAL_INFO'
}

/**
 * Configuration for log sanitization
 */
export interface LogSanitizerConfig {
  // Whether to enable sanitization
  enabled: boolean;
  
  // Patterns to mask (in addition to built-in patterns)
  customPatterns?: Record<string, RegExp>;
  
  // Replacement character for masked data
  maskChar?: string;
  
  // Number of characters to show at the beginning and end of masked data
  revealChars?: number;
  
  // Keys to completely remove from objects (not just mask)
  removeKeys?: string[];
  
  // Maximum depth to sanitize in nested objects
  maxDepth?: number;
}

/**
 * Default configuration for log sanitization
 */
const DEFAULT_CONFIG: LogSanitizerConfig = {
  enabled: true,
  maskChar: '*',
  revealChars: 2,
  removeKeys: ['password', 'token', 'secret', 'key', 'credential', 'credit_card', 'ssn'],
  maxDepth: 5
};

/**
 * Built-in patterns for sensitive data
 */
const SENSITIVE_PATTERNS: Record<SensitiveDataType, RegExp> = {
  [SensitiveDataType.USER_ID]: /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, // UUID pattern
  [SensitiveDataType.EMAIL]: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g,
  [SensitiveDataType.PASSWORD]: /\b(?:password|passwd)\s*[:=]\s*["']?[^"'\s]+["']?/gi,
  [SensitiveDataType.AUTH_TOKEN]: /\b(?:token|jwt|bearer)\s*[:=]\s*["']?[A-Za-z0-9._-]+["']?/gi,
  [SensitiveDataType.SESSION_ID]: /\b(?:session|sid)\s*[:=]\s*["']?[A-Za-z0-9._-]+["']?/gi,
  [SensitiveDataType.PROFILE_DATA]: /\b(?:profile|user)\s*[:=]\s*{[^}]+}/gi,
  [SensitiveDataType.RESPONSE_DATA]: /\b(?:response|result)\s*[:=]\s*{[^}]+}/gi,
  [SensitiveDataType.CREDIT_CARD]: /\b(?:\d{4}[-\s]?){3}\d{4}\b/g,
  [SensitiveDataType.SSN]: /\b\d{3}-\d{2}-\d{4}\b/g,
  [SensitiveDataType.API_KEY]: /\b(?:api[_-]?key|apikey)\s*[:=]\s*["']?[A-Za-z0-9._-]+["']?/gi,
  [SensitiveDataType.DATABASE_CREDENTIALS]: /\b(?:db|database)\s*[:=]\s*{[^}]+}/gi,
  [SensitiveDataType.PERSONAL_INFO]: /\b(?:name|address|phone|dob|birth)\s*[:=]\s*["']?[^"'\s]+["']?/gi
};

/**
 * Current configuration
 */
let currentConfig: LogSanitizerConfig = { ...DEFAULT_CONFIG };

/**
 * Configure the log sanitizer
 * 
 * @param config Configuration options
 */
export function configureLogSanitizer(config: Partial<LogSanitizerConfig>): void {
  currentConfig = { ...DEFAULT_CONFIG, ...config };
}

/**
 * Mask a string value
 * 
 * @param value The string to mask
 * @returns The masked string
 */
function maskValue(value: string): string {
  if (!value || value.length <= currentConfig.revealChars! * 2) {
    return value;
  }
  
  const start = value.substring(0, currentConfig.revealChars!);
  const end = value.substring(value.length - currentConfig.revealChars!);
  const masked = currentConfig.maskChar!.repeat(Math.min(8, value.length - (currentConfig.revealChars! * 2)));
  
  return `${start}${masked}${end}`;
}

/**
 * Sanitize a string by masking sensitive data
 * 
 * @param input The string to sanitize
 * @returns The sanitized string
 */
export function sanitizeLogString(input: string): string {
  if (!currentConfig.enabled || !input) {
    return input;
  }
  
  let sanitized = input;
  
  // Skip sanitization for error messages that contain common error keywords
  // This prevents over-sanitization of debugging information
  const errorKeywords = ['Failed to', 'Error:', 'Exception:', 'Cannot', 'Unable to', 'Invalid', 'Missing'];
  const isErrorMessage = errorKeywords.some(keyword => input.includes(keyword));
  
  if (isErrorMessage) {
    // Only sanitize UUIDs in error messages, leave the rest intact for debugging
    sanitized = sanitized.replace(SENSITIVE_PATTERNS[SensitiveDataType.USER_ID], (match) => maskValue(match));
    return sanitized;
  }
  
  // Apply built-in patterns for non-error messages
  Object.entries(SENSITIVE_PATTERNS).forEach(([type, pattern]) => {
    sanitized = sanitized.replace(pattern, (match) => {
      // Special handling for JSON-like structures
      if (match.includes('{') && match.includes('}')) {
        return match.replace(/{[^}]+}/, '{...}');
      }
      
      // Extract just the value part for patterns with key:value format
      if (match.includes(':') || match.includes('=')) {
        const parts = match.split(/[:=]/);
        if (parts.length > 1) {
          const key = parts[0].trim();
          const value = parts.slice(1).join(':').trim().replace(/^["']|["']$/g, '');
          return `${key}: "${maskValue(value)}"`;
        }
      }
      
      return maskValue(match);
    });
  });
  
  // Apply custom patterns
  if (currentConfig.customPatterns) {
    Object.entries(currentConfig.customPatterns).forEach(([key, pattern]) => {
      sanitized = sanitized.replace(pattern, (match) => maskValue(match));
    });
  }
  
  return sanitized;
}

/**
 * Sanitize an object by masking sensitive data
 * 
 * @param obj The object to sanitize
 * @param depth Current recursion depth
 * @returns The sanitized object
 */
export function sanitizeLogObject<T>(obj: T, depth: number = 0): any {
  if (!currentConfig.enabled) {
    return obj;
  }
  
  if (!obj || typeof obj !== 'object' || depth > currentConfig.maxDepth!) {
    return obj;
  }
  
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeLogObject(item, depth + 1));
  }
  
  const result: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(obj as Record<string, any>)) {
    // Skip keys that should be removed
    if (currentConfig.removeKeys!.some(removeKey => 
      key.toLowerCase().includes(removeKey.toLowerCase()))) {
      result[key] = '[REDACTED]';
      continue;
    }
    
    // Handle nested objects
    if (value && typeof value === 'object') {
      result[key] = sanitizeLogObject(value, depth + 1);
      continue;
    }
    
    // Handle string values
    if (typeof value === 'string') {
      // Check if this key is likely to contain sensitive data
      const isSensitiveKey = key.toLowerCase().match(
        /(user|id|email|profile|session|token|key|secret|password|credential|ssn|credit|card|address|phone|name|dob|birth)/
      );
      
      if (isSensitiveKey) {
        result[key] = maskValue(value);
      } else {
        // Still check the value against patterns
        const originalValue = value;
        const sanitizedValue = sanitizeLogString(value);
        result[key] = sanitizedValue !== originalValue ? sanitizedValue : value;
      }
    } else {
      result[key] = value;
    }
  }
  
  return result;
}

/**
 * Create a sanitized logger that wraps console.log
 * 
 * @returns An object with sanitized logging methods
 */
export function createSanitizedLogger() {
  return {
    /**
     * Log a message with sanitized values
     */
    log: (...args: any[]) => {
      if (!currentConfig.enabled) {
        console.log(...args);
        return;
      }
      
      const sanitizedArgs = args.map(arg => {
        if (typeof arg === 'string') {
          return sanitizeLogString(arg);
        } else if (arg && typeof arg === 'object') {
          return sanitizeLogObject(arg);
        }
        return arg;
      });
      
      console.log(...sanitizedArgs);
    },
    
    /**
     * Log an error with sanitized values
     */
    error: (...args: any[]) => {
      if (!currentConfig.enabled) {
        console.error(...args);
        return;
      }
      
      const sanitizedArgs = args.map(arg => {
        if (typeof arg === 'string') {
          return sanitizeLogString(arg);
        } else if (arg && typeof arg === 'object') {
          return sanitizeLogObject(arg);
        }
        return arg;
      });
      
      console.error(...sanitizedArgs);
    },
    
    /**
     * Log a warning with sanitized values
     */
    warn: (...args: any[]) => {
      if (!currentConfig.enabled) {
        console.warn(...args);
        return;
      }
      
      const sanitizedArgs = args.map(arg => {
        if (typeof arg === 'string') {
          return sanitizeLogString(arg);
        } else if (arg && typeof arg === 'object') {
          return sanitizeLogObject(arg);
        }
        return arg;
      });
      
      console.warn(...sanitizedArgs);
    },
    
    /**
     * Log info with sanitized values
     */
    info: (...args: any[]) => {
      if (!currentConfig.enabled) {
        console.info(...args);
        return;
      }
      
      const sanitizedArgs = args.map(arg => {
        if (typeof arg === 'string') {
          return sanitizeLogString(arg);
        } else if (arg && typeof arg === 'object') {
          return sanitizeLogObject(arg);
        }
        return arg;
      });
      
      console.info(...sanitizedArgs);
    }
  };
}

/**
 * Default sanitized logger instance
 */
export const safeLog = createSanitizedLogger();

/**
 * Utility function to sanitize an error object for logging
 * 
 * @param error The error to sanitize
 * @returns A sanitized error object safe for logging
 */
export function sanitizeError(error: any): any {
  if (!error) return error;
  
  const sanitizedError: Record<string, any> = {
    name: error.name,
    message: sanitizeLogString(error.message),
    stack: error.stack ? sanitizeLogString(error.stack) : undefined
  };
  
  // Add any additional properties from the error
  if (typeof error === 'object') {
    for (const key of Object.keys(error)) {
      if (!sanitizedError[key]) {
        const value = error[key];
        if (typeof value === 'string') {
          sanitizedError[key] = sanitizeLogString(value);
        } else if (value && typeof value === 'object') {
          sanitizedError[key] = sanitizeLogObject(value);
        } else {
          sanitizedError[key] = value;
        }
      }
    }
  }
  
  return sanitizedError;
}